import {
  flatToTree as datasheetFlatToTree,
  treeToFlat as datasheetTreeToFlat
} from 'src/utils/form/datasheet';
import {
  flatToTree as checklistFlatToTree,
  treeToFlat as checklistTreeToFlat
} from 'src/utils/form/checklist';
import { notification } from 'src/components/Notification';
import i18next from 'i18next';
import { SurveyType } from 'src/utils/data/types';

const removeQuestionsByIds = (sections, field, ids, flatToTreeCallback, treeToFlatCallback) => {
  sections.forEach(section => {
    // eslint-disable-next-line no-param-reassign
    section.questions = section.questions.filter(c => !ids.includes(c[field])).map((q, index) => ({
      ...q,
      position: index,
    }));
    // eslint-disable-next-line no-param-reassign
    section.questions = treeToFlatCallback(flatToTreeCallback(section.questions));
  });
};

export const removeQuestionFromSection = (
  sectionPosition,
  questionPosition,
  sectionsArray,
  type,
  setField,
  questions = [],
) => {

  const newSections = [...sectionsArray];

  if (type === SurveyType.Datasheet) {
    const { datasheetQuestionId } = newSections[sectionPosition].questions[questionPosition];
    const dependentDatasheetQuestionIds = questions.filter(datasheetQuestion => {
      let dependsOn = datasheetQuestion.currentVersion?.dependentDropdownSettings?.dependsOn;
      let iteration = 0;
      while (dependsOn && (iteration < 100)) {
        if (dependsOn === datasheetQuestionId) {
          return true;
        }
        // eslint-disable-next-line no-loop-func
        const dependsOnQuestion = questions.find(dq => dq.id === dependsOn);
        dependsOn = dependsOnQuestion?.currentVersion?.dependentDropdownSettings?.dependsOn

        iteration += 1;
      }

      return false;
    }).map(c => c.id);

    if (dependentDatasheetQuestionIds.length > 0) {
      removeQuestionsByIds(newSections, 'datasheetQuestionId', dependentDatasheetQuestionIds, datasheetFlatToTree, datasheetTreeToFlat);

      notification.info(i18next.t('DATASHEET_DEPENDENT_QUESTION_REMOVED', {
        questionIds: dependentDatasheetQuestionIds.join(', '),
      }), {
        autoClose: 10000,
      });
    }
  }

  newSections[sectionPosition].questions.splice(questionPosition, 1);
  newSections[sectionPosition].questions = newSections[
    sectionPosition
  ].questions.map((q, index) => ({ ...q, position: index }));

  if (type === SurveyType.Checklist) {
    newSections[sectionPosition].questions = checklistTreeToFlat(
      checklistFlatToTree(newSections[sectionPosition].questions || []),
    );
  } else {
    newSections[sectionPosition].questions = datasheetTreeToFlat(
      datasheetFlatToTree(newSections[sectionPosition].questions || []),
    );
  }

  setField(newSections);
};

export const moveQuestionInSection = (
  { index: from, droppableId: fromPositionRaw },
  { index: to, droppableId: toPositionRaw },
  sectionsArray,
  type,
  setField,
  expandSection,
) => {
  const fromPosition = +fromPositionRaw.replace('droppable-', '');
  const toPosition = +toPositionRaw.replace('droppable-', '');

  if (fromPosition === toPosition && from === to) {
    return;
  }

  const newSections = [...sectionsArray];
  const { nestingLevel, ...question } = newSections[
    fromPosition
  ].questions[from];
  newSections[fromPosition].questions.splice(from, 1);

  const fromQuestionId = type === SurveyType.Checklist ? (
    (question.conditions || [])[0] || { checklistQuestionId: null }
  ).checklistQuestionId : (
    (question.conditions || [])[0] || { datasheetQuestionId: null }
  ).datasheetQuestionId;

  const toQuestionId = type === SurveyType.Checklist ? (
    ((newSections[toPosition].questions[to] &&
      newSections[toPosition].questions[to].conditions) ||
      [])[0] || { checklistQuestionId: null }
  ).checklistQuestionId : (
    ((newSections[toPosition].questions[to] &&
      newSections[toPosition].questions[to].conditions) ||
      [])[0] || { datasheetQuestionId: null }
  ).datasheetQuestionId;

  if (fromPosition === toPosition && fromQuestionId === toQuestionId) {
    newSections[toPosition].questions.splice(to, 0, {
      ...question,
      nestingLevel,
    });
  } else {
    newSections[toPosition].questions.splice(to, 0, {
      ...question,
      nestingLevel: 0,
    });

    let foundLess = false;
    const childrenSliceLength = newSections[
      fromPosition
    ].questions.filter((q, index) => {
      const result =
        !foundLess && index >= from && q.nestingLevel > nestingLevel;
      if (index >= from && q.nestingLevel <= nestingLevel) {
        foundLess = true;
      }
      return result;
    }).length;
      if (childrenSliceLength > 0) {
      newSections[toPosition].questions = [
        ...newSections[toPosition].questions,
      ];
    } 
  }

  newSections[fromPosition].questions = newSections[
    fromPosition
  ].questions.map((q, index) => ({ ...q, position: index }));
  if (fromPosition !== toPosition) {
    newSections[toPosition].questions = newSections[
      toPosition
    ].questions.map((q, index) => ({
      ...q,
      position: index,
    }));

  }
  newSections[fromPosition].questions = type === SurveyType.Checklist ? checklistTreeToFlat(
    checklistFlatToTree(newSections[fromPosition].questions || []),
  ) : datasheetTreeToFlat(
    datasheetFlatToTree(newSections[fromPosition].questions || []),
  );

  setField(newSections);
  expandSection(toPosition);
};

export const removeConditionInSection = (
  sectionPosition,
  questionPosition,
  conditionPosition,
  sectionsArray,
  type,
  setField,
) => {
  const newSections = sectionsArray;

  newSections[sectionPosition].questions[
    questionPosition
  ].conditions.splice(conditionPosition, 1);
  newSections[sectionPosition].questions[
    questionPosition
  ].conditions = newSections[sectionPosition].questions[
    questionPosition
  ].conditions.map((q, index) => ({ ...q, position: index }));
  newSections[sectionPosition].questions = type === SurveyType.Checklist ? checklistTreeToFlat(
    checklistFlatToTree(newSections[sectionPosition].questions || []),
  ) : datasheetTreeToFlat(
    datasheetFlatToTree(newSections[sectionPosition].questions || []),
  );

  setField(newSections.map(v => v));
};

export const getOpenedQuestion = (sections, infoOpened) => {
  const findedSection = (sections || []).find(
    section => section.position === infoOpened.section,
  );
  if (!findedSection) {
    return null;
  }
  return findedSection.questions[infoOpened.questionPosition];
};

export const getOpenedSection = (sections, infoOpened) => (sections || [])
  .find(section => section.position === infoOpened.section);

export const getActiveReportReviewNote = (reportReviewData, activeQuestion) => activeQuestion &&
  reportReviewData?.ReportReview &&
  reportReviewData.ReportReview.reportReviewNotes
    .filter(note => note.typeQuestionId === activeQuestion.id)[0];

export const getJobParamFromCompanies = (companies, type) => companies.find(c => {
  if (c.companyTypeId) {
    const companyType = c.company.currentVersion.companyTypes.find(cc => cc.currentVersion.name === type);
    return companyType && companyType.id === c.companyTypeId;
  }

  return c.company.currentVersion.companyTypes.map(cc => cc.currentVersion.name).includes(type);
});

export const getQuestionWithResettedAlerts = ({
  checklistQuestions,
  forceUpdateAlerts,
  negativeResultCategory,
  question,
  resultFamilies,
  resultFamilyId,
  severities,
}) => {
  const resultFamily =
    resultFamilies &&
    resultFamilies.find(r => r.id === resultFamilyId);
  const negativeResultFamilyOutcomes = negativeResultCategory &&
    resultFamily?.currentVersion?.resultFamilyOutcomes.filter(
      rfo => rfo.resultCategoryId === negativeResultCategory.id
    );
  const severityOptions = severities.map(a => ({
    value: a.id,
    label: a.name,
  }));

  const updatedQuestion = { ...question };
  const defaultChecklistQuestion = checklistQuestions.find(x => {
    return x.id === question.checklistQuestionId;
  });

  const negativeAlert = defaultChecklistQuestion.currentVersion.negativeResultAlert;
  const defaultSeverity = severityOptions
    .find(c => c.value === defaultChecklistQuestion.currentVersion.negativeResultSeverityId);

  if (!updatedQuestion.resultFamilyId || forceUpdateAlerts) {
    updatedQuestion.alerts = negativeResultFamilyOutcomes.map(res => ({
      negativeAlert,
      negativeSeverityId: defaultSeverity.value,
      resultFamilyOutcomeId: res.id,
    }));
  }

  return updatedQuestion;
};

export const getChecklistSectionsWithResettedAlerts = ({
  checklistQuestions,
  checklistSections,
  negativeResultCategory,
  resultFamilies,
  resultFamilyId,
  severities,
}) => checklistSections.map(section => ({
  ...section,
  questions: section.questions.map(question => getQuestionWithResettedAlerts({
    checklistQuestions,
    negativeResultCategory,
    question,
    resultFamilies,
    resultFamilyId,
    severities,
  }))
}));

export const getSurveySectionsKeyNameByType = type => {
  switch (type) {
    case SurveyType.Checklist:
      return 'checklistSections';
    case SurveyType.Datasheet:
      return 'datasheetSections';
    case SurveyType.CustomPanel:
      return 'customPanelSections';
    default:
      return null;
  }
};
